import { useEffect, useState, type FormEvent } from 'react'
import { Link, Navigate } from 'react-router-dom'

import { ApiClientError } from '@/api/client'
import { changeMyPassword, fetchMyProfile, updateMyProfile } from '@/api/authApi'
import { useAuth } from '@/auth/AuthContext'

import authStyles from './AuthPages.module.css'
import styles from './PortalPages.module.css'

/** Shared profile + password page for vendors and admins. */
export function ProfilePage() {
  const { isAuthenticated, isAdmin, user, updateStoredUser, logout } = useAuth()
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const [businessName, setBusinessName] = useState('')
  const [username, setUsername] = useState('')
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [saved, setSaved] = useState('')
  const [busy, setBusy] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!isAuthenticated) return
    void (async () => {
      setLoading(true)
      setError('')
      try {
        const result = await fetchMyProfile()
        setFullName(result.user.full_name)
        setEmail(result.user.email)
        setWhatsapp(result.user.whatsapp_number || '')
        setBusinessName(result.user.business_name || '')
        setUsername(result.user.username)
        updateStoredUser(result.user)
      } catch (err) {
        setError(err instanceof ApiClientError ? err.message : 'Could not load profile.')
      } finally {
        setLoading(false)
      }
    })()
  }, [isAuthenticated, updateStoredUser])

  if (!isAuthenticated) return <Navigate to="/login" replace />

  async function onSaveProfile(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    setSaved('')
    try {
      const result = await updateMyProfile({
        full_name: fullName,
        email,
        whatsapp_number: whatsapp || null,
        business_name: isAdmin ? null : businessName || null,
      })
      updateStoredUser(result.user)
      setSaved('Profile saved.')
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not save profile.')
    } finally {
      setBusy(false)
    }
  }

  async function onChangePassword(event: FormEvent) {
    event.preventDefault()
    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters.')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.')
      return
    }
    setBusy(true)
    setError('')
    setSaved('')
    try {
      await changeMyPassword({
        current_password: currentPassword,
        new_password: newPassword,
      })
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setSaved('Password updated.')
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not change password.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className={styles.page}>
      <header className={styles.header}>
        <div>
          <p className={styles.brand}>Oikos Market Day</p>
          <h1 className={styles.title}>My profile</h1>
          <p className={styles.lead}>
            Update your contact details and password
            {user ? ` for @${user.username}` : ''}.
          </p>
        </div>
        <div className={styles.actions}>
          <Link className={styles.secondary} to={isAdmin ? '/admin' : '/vendor'}>
            Back
          </Link>
          <button type="button" className={styles.ghost} onClick={logout}>
            Sign out
          </button>
        </div>
      </header>

      {error ? <p className={styles.error}>{error}</p> : null}
      {saved ? <p className={styles.metaOk}>{saved}</p> : null}
      {loading ? <p className={styles.empty}>Loading profile…</p> : null}

      {!loading ? (
        <>
          <form className={styles.formGrid} onSubmit={(event) => void onSaveProfile(event)}>
            <h2 className={styles.itemTitle}>Profile details</h2>
            <p className={styles.meta}>Username: @{username} (cannot be changed here)</p>

            <label className={authStyles.label}>
              Full name
              <input
                className={authStyles.input}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            </label>
            <label className={authStyles.label}>
              Email
              <input
                className={authStyles.input}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
            <label className={authStyles.label}>
              WhatsApp number{!isAdmin ? '' : ' (optional)'}
              <input
                className={authStyles.input}
                type="tel"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                required={!isAdmin}
              />
            </label>
            {!isAdmin ? (
              <label className={authStyles.label}>
                Business name
                <input
                  className={authStyles.input}
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                />
              </label>
            ) : null}

            <div className={styles.actions}>
              <button className={authStyles.submit} type="submit" disabled={busy}>
                {busy ? 'Saving…' : 'Save profile'}
              </button>
            </div>
          </form>

          <form
            className={styles.formGrid}
            style={{ marginTop: '1.5rem' }}
            onSubmit={(event) => void onChangePassword(event)}
          >
            <h2 className={styles.itemTitle}>Change password</h2>
            <label className={authStyles.label}>
              Current password
              <input
                className={authStyles.input}
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </label>
            <label className={authStyles.label}>
              New password
              <input
                className={authStyles.input}
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                minLength={6}
                required
                autoComplete="new-password"
              />
            </label>
            <label className={authStyles.label}>
              Confirm new password
              <input
                className={authStyles.input}
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                minLength={6}
                required
                autoComplete="new-password"
              />
            </label>
            <div className={styles.actions}>
              <button className={authStyles.submit} type="submit" disabled={busy}>
                {busy ? 'Updating…' : 'Update password'}
              </button>
            </div>
          </form>
        </>
      ) : null}
    </section>
  )
}
